import { DateTime } from 'luxon';
import { useReducedMotion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { SlideProps } from '@/lib/whats-new/edition-types';
import {
  estreiaFingerPath,
  fingerIndexAt,
  type FingerPath,
} from '@/lib/whats-new/finger-path';
import { AdminPhoneIllustration } from '@/components/whats-new/editions/estreia/admin-phone-illustration';
import { ScaledDevice } from '@/components/whats-new/editions/estreia/scaled-device';
import { useEstreiaFacts } from '@/components/whats-new/editions/estreia/use-estreia-facts';
import { phoneVM } from '@/components/whats-new/editions/estreia/view-models';
import { Eyebrow } from '@/components/whats-new/slides/eyebrow';
import { Rise } from '@/components/whats-new/slides/rise';
import {
  SlideLayout,
  SlideLead,
  SlideTitle,
} from '@/components/whats-new/slides/slide-layout';
import { WordReveal } from '@/components/whats-new/slides/word-reveal';

/** The finger lands after the title and the phone have come in. */
export const FINGER_START_MS = 1600;
/** Lifted between two drags. */
export const FINGER_GAP_MS = 1400;
export const FINGER_CYCLES = 3;

/**
 * Day under the finger over time: 3 drags of `path`, `FINGER_GAP_MS` apart,
 * `null` while it is lifted (the card shows the total). Nothing moves under
 * reduced motion (NEWS-09); leaving the slide cancels it.
 */
function useFingerIndex(path: FingerPath | null): number | null {
  const reduceMotion = useReducedMotion() ?? false;
  const [index, setIndex] = useState<number | null>(null);

  useEffect(() => {
    setIndex(null);
    if (reduceMotion || !path) return;
    const cycleMs = path.releaseMs + FINGER_GAP_MS;
    let frame = 0;
    let start: number | null = null;
    const step = (now: number) => {
      start ??= now;
      const elapsed = now - start;
      const cycle = Math.floor(elapsed / cycleMs);
      if (cycle >= FINGER_CYCLES) {
        setIndex(null);
        return;
      }
      setIndex(fingerIndexAt(elapsed - cycle * cycleMs, path));
      frame = requestAnimationFrame(step);
    };
    const timer = setTimeout(() => {
      frame = requestAnimationFrame(step);
    }, FINGER_START_MS);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(frame);
    };
  }, [path, reduceMotion]);

  return index;
}

/**
 * 2 · No celular (NEWS-29): the admin's mobile Início with the store's
 * numbers inside the iPhone; a finger drags across the revenue chart to the
 * top campaign's peak and the second one, and the reading follows it.
 */
export function PhoneSlide(_props: SlideProps) {
  const { LL, locale } = useI18nContext();
  const facts = useEstreiaFacts();
  const vm = phoneVM(facts, locale);
  const now = DateTime.fromISO(facts.period.current.to);

  const peakDay =
    vm.revenue.state === 'ready' ? vm.revenue.value.peakDay : null;
  const secondDay =
    vm.revenue.state === 'ready' ? vm.revenue.value.secondDay : null;
  const path = useMemo(
    () =>
      peakDay === null || secondDay === null
        ? null
        : estreiaFingerPath(peakDay, secondDay),
    [peakDay, secondDay]
  );
  const fingerIndex = useFingerIndex(path);

  return (
    <SlideLayout
      copy={
        <>
          <Rise index={0}>
            <Eyebrow lead={LL.whatsNew.editions.estreia.phone.eyebrow()} />
          </Rise>
          <SlideTitle>
            <WordReveal text={LL.whatsNew.editions.estreia.phone.title()} />
          </SlideTitle>
          <Rise index={2}>
            <SlideLead>
              {LL.whatsNew.editions.estreia.phone.lead({
                store: vm.store.name,
              })}
            </SlideLead>
          </Rise>
        </>
      }
      visual={
        <Rise index={1} className="grid place-items-center">
          <ScaledDevice className="[--device-scale:0.56] max-md:[--device-scale:0.42] [@media(min-width:768px)_and_(max-height:800px)]:[--device-scale:0.48]">
            <AdminPhoneIllustration
              vm={vm}
              now={now}
              fingerIndex={fingerIndex}
            />
          </ScaledDevice>
        </Rise>
      }
    />
  );
}
